import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { v4 as uuidv4 } from 'uuid';
import { addDays, startOfWeek } from 'date-fns';
import { PrintScheduleComponent } from './print-schedule-component';
import { WithCitizenContext } from '../../../citizen/citizen-context';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { PrintScheduleState, PrintScheduleStateErrors } from './interfaces';
import { useBroadcastContext, useErrorSnackbar } from '../../../../hooks';
import { WindowService } from '../../../../components/window-service/window-service';
import { standAlonePathName } from '../../../area-pathnames';
import { citizenAreaPathName, citizenLetterGuide, communicationTabPathName } from '../../../citizen/authority/citizen-authority-area-pathnames';
import { ApexExtraLetterData } from '../../../citizen/common/communication/components/send-letter-guide/external-interfaces';
import { ScheduleDisplayMode } from '../../../../api-service/clients';
import { useFeatureToggles } from '../../../../feature-toggles';

const client = moduleClientFactory.createAttendanceClient();

const coverLetterTemplateCode = 'Flg-Skema';

const defaultInclusions = { includeAbsences: false, includeInterviews: true };

const displayModes = [ScheduleDisplayMode.Day, ScheduleDisplayMode.Week, ScheduleDisplayMode.Month, ScheduleDisplayMode.List];
const displayModesWithWeekScheme = [...displayModes, ScheduleDisplayMode.WeekScheme];

interface IPrintScheduleProps {
  sendToCitizen?: boolean;
}

export function PrintSchedule({ sendToCitizen = false }: Readonly<IPrintScheduleProps>) {
  const { citizenId } = useParams();
  const { isFeatureEnabled } = useFeatureToggles();

  const [state, setState] = useImmer<PrintScheduleState>({ citizenId, ...defaultInclusions });
  const [errors, setErrors] = useState<PrintScheduleStateErrors>({} as PrintScheduleStateErrors);
  const [isLoading, setIsLoading] = useState(false);
  const [sendError, setSendError] = useState<unknown>();
  const pendingLetter = useRef<{ communicationId: string; letterData: ApexExtraLetterData }>();
  const { send: sendLetterData, subscribe: subscribeLetterData } = useBroadcastContext('SendLetterData');

  useErrorSnackbar(sendError);

  subscribeLetterData(({ data }) => {
    if (pendingLetter.current && data === `ReadyToReceive-${pendingLetter.current.communicationId}`) {
      sendLetterData(JSON.stringify(pendingLetter.current.letterData));
      pendingLetter.current = undefined;
    }
  });

  async function handlePrintScheduleClick() {
    try {
      setIsLoading(true);
      const { includeAbsences, includeInterviews, displayMode, endDate, startDate } = state;
      if (displayMode && startDate && endDate) {
        const result = await client.getCitizenSchedulePdf({ citizenId, displayMode, endDate, includeAbsences, includeInterviews, startDate });
        const pdfDocumentBlob = new Blob([result.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(pdfDocumentBlob);
        link.target = '_blank';
        link.click();
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSendScheduleClick() {
    const { includeAbsences, includeInterviews, displayMode, endDate, startDate } = state;
    if (!displayMode || !startDate || !endDate) return;

    try {
      setIsLoading(true);
      setSendError(undefined);
      const { documentId } = await client.createCitizenScheduleLetterAttachment({ citizenId, displayMode, endDate, includeAbsences, includeInterviews, startDate });
      const communicationId = uuidv4();
      pendingLetter.current = {
        communicationId,
        letterData: {
          usePreselectedTemplate: true,
          journalNoteTitle: 'Borgers skema sendt',
          notifyOpenerOnGuideComplete: false,
          useActivityTemplates: false,
          preAttachedDocumentIds: [documentId],
        },
      };
      WindowService.open(
        `${standAlonePathName}/${citizenAreaPathName}/${citizenId}/${communicationTabPathName}/${citizenLetterGuide}` +
          `?preSelectedTemplateCode=${encodeURIComponent(coverLetterTemplateCode)}&waitForData=true&communicationId=${communicationId}`
      );
    } catch (error) {
      setSendError(error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleValidate() {
    const { displayMode, endDate, startDate } = state;

    const tmpErrors = {} as PrintScheduleStateErrors;
    let isValid = true;

    if (!displayMode) {
      tmpErrors.displayMode = 'Visning skal vælges';
      isValid = false;
    }

    if (!startDate) {
      tmpErrors.startDate = 'Startdato skal angives';
      isValid = false;
    }

    if (!endDate && displayMode !== ScheduleDisplayMode.WeekScheme) {
      tmpErrors.endDate = 'Slutdato skal angives';
      isValid = false;
    }

    setErrors({ ...tmpErrors });

    return isValid;
  }

  function handleStateChange<K extends keyof PrintScheduleState>(name: K, value: PrintScheduleState[K]) {
    setState((draft: PrintScheduleState) => {
      const wasWeekScheme = draft.displayMode === ScheduleDisplayMode.WeekScheme;
      draft[name] = value;
      if (draft.displayMode === ScheduleDisplayMode.WeekScheme) {
        restrictToWorkWeek(draft);
      } else if (wasWeekScheme) {
        draft.includeAbsences = defaultInclusions.includeAbsences;
        draft.includeInterviews = defaultInclusions.includeInterviews;
      }
    });

    const displayMode = name === 'displayMode' ? value : state.displayMode;
    setErrors({ ...errors, [name]: '', ...(displayMode === ScheduleDisplayMode.WeekScheme && { endDate: '' }) });
  }

  return (
    <WithCitizenContext citizenId={citizenId}>
      <PrintScheduleComponent
        printSetup={state}
        displayModes={isFeatureEnabled('EnableGenericPdfScheme') ? displayModesWithWeekScheme : displayModes}
        isEditmode={false}
        isLoading={isLoading}
        title={sendToCitizen ? 'Send til borger' : 'Udskriv'}
        completeLabel={sendToCitizen ? 'Send skema' : 'Udfør'}
        onPrintScheduleClick={sendToCitizen ? handleSendScheduleClick : handlePrintScheduleClick}
        onValidate={handleValidate}
        onPrintSetupChange={handleStateChange}
        errors={errors}
      />
    </WithCitizenContext>
  );
}

function restrictToWorkWeek(draft: PrintScheduleState) {
  draft.includeAbsences = false;
  draft.includeInterviews = false;

  if (!draft.startDate) {
    draft.endDate = undefined;
    return;
  }

  const monday = startOfWeek(new Date(draft.startDate), { weekStartsOn: 1 });
  draft.startDate = monday;
  draft.endDate = addDays(monday, 4);
}
